import React from 'react';

import { MultiSelect } from 'components';
import classNames from 'classnames';
import DraggableOptionsList from 'components/draggable-options-list/draggable-options-list';

import {
	FleetRuleRightSideHandlers,
	FleetRuleRightSideOptions,
	FeatureFlags,
	FleetRuleRightSideTranslations,
	RadioValues,
	shouldShowRightSideMultiselect,
	shouldDisableDropdownOptions
} from '../fleet-rule-utils';
import { CheckboxAttribute } from './checkbox-attribute';
import { FleetRadioGroup } from './fleet-radio-group';
import { FleetDropdownType } from './fleet-dropdown-group-type';

import styles from '../fleet-rule.module.scss';

interface FleetRuleRightSideProps {
	disabled?: boolean;
	translations: FleetRuleRightSideTranslations;
	fleetRuleRightSideOptions: FleetRuleRightSideOptions;
	fleetRuleRightSideHandlers: FleetRuleRightSideHandlers;
	featureFlags?: FeatureFlags;
}

export const INPUT_WIDTH = '100%';

export const FleetRuleRightSide = ({
	disabled = false,
	translations,
	fleetRuleRightSideOptions,
	fleetRuleRightSideHandlers,
	featureFlags
}: FleetRuleRightSideProps) => (
	<>
		<div
			data-test-id="fleet-router-rule-body-right-side-title"
			className={classNames(styles['header'], { [styles['disabled']]: disabled })}
		>
			{translations.assignmentMethod}
		</div>
		<div data-test-id="fleet-router-rule-body-right-side-radio-group-top">
			<label
				className={classNames(styles['radio-title'], {
					disabled: disabled
				})}
			>
				{translations.assignBy}
			</label>
			<FleetRadioGroup
				disabled={disabled}
				handleChange={fleetRuleRightSideHandlers.handleMethodChange}
				options={fleetRuleRightSideOptions.methodRadioOptions}
				value={fleetRuleRightSideOptions.methodRadioValue}
			/>
			{featureFlags?.pickupEtaAvailable && fleetRuleRightSideOptions.methodRadioValue === RadioValues.FASTEST && (
				<FleetDropdownType
					disabled={disabled}
					handleChange={fleetRuleRightSideHandlers.handleMethodDropdownChange}
					options={fleetRuleRightSideOptions.methodDropdownTypeOptions}
					value={fleetRuleRightSideOptions.methodDropdownTypeValue}
					title={translations.dropdownTypeTitle}
					tooltipText={translations.dropdownTypeTooltip}
				/>
			)}
		</div>
		{(featureFlags?.pickupEtaAvailable
			? shouldShowRightSideMultiselect(
					fleetRuleRightSideOptions.methodRadioValue,
					fleetRuleRightSideOptions.methodDropdownTypeValue
			  )
			: [RadioValues.PRIORITY, RadioValues.RATIO].indexOf(fleetRuleRightSideOptions.methodRadioValue) !== -1) && (
			<div className={styles['right-side-main']}>
				<div data-test-id="fleet-router-rule-body-right-side-multiselect" className={styles['multiselect']}>
					<div
						className={classNames(styles['multiselect-title'], {
							[styles['disabled']]: disabled
						})}
					>
						{translations.selectCarrier}
					</div>
					<MultiSelect
						disabled={disabled}
						showArrow
						style={{ width: INPUT_WIDTH }}
						placeholder={translations.multiselectPlaceholder}
						options={fleetRuleRightSideOptions.multiSelectOptions}
						value={disabled ? [] : fleetRuleRightSideOptions.multiSelectValue}
						allowClear
						byKey
						onChange={fleetRuleRightSideHandlers.handleMultiselectChange}
						shouldDisableOptions={
							featureFlags?.pickupEtaAvailable &&
							shouldDisableDropdownOptions({
								methodRadioValue: fleetRuleRightSideOptions.methodRadioValue,
								methodDropdownTypeValue: fleetRuleRightSideOptions.methodDropdownTypeValue
							})
						}
					/>
				</div>

				{featureFlags?.enableRatioPercentage && fleetRuleRightSideOptions.showPercentageTips && !disabled && (
					<div
						className={styles['ratio-total-percentage-label-value-tip']}
						data-test-id="ratio-total-percentage-label-value-tip"
					>
						<div className={styles['ratio-total-percentage-label-value']}>
							<div
								className={styles['ratio-total-percentage-label']}
								data-test-id="ratio-total-percentage-label"
							>
								{translations.totalPercentageLabel}
							</div>
							<div
								className={classNames(styles['ratio-total-percentage-value'], {
									[styles['ratio-total-percentage-value-error']]:
										fleetRuleRightSideOptions.markInvalidPercentage ||
										fleetRuleRightSideOptions.invalidPercents
								})}
								data-test-id={
									fleetRuleRightSideOptions.invalidPercents
										? 'ratio-total-percentage-value-error'
										: 'ratio-total-percentage-value'
								}
							>
								{`${fleetRuleRightSideOptions.totalPercentage}%`}
							</div>
						</div>
						<div className={styles['ratio-total-percentage-tip']} data-test-id="ratio-total-percentage-tip">
							{translations.ratioTip}
						</div>
					</div>
				)}

				<DraggableOptionsList
					disabled={disabled}
					options={fleetRuleRightSideOptions.draggableOptions}
					handleDragOption={fleetRuleRightSideHandlers.handleDragOption}
					handlePercentageInput={fleetRuleRightSideHandlers.handlePercentageInput}
					shouldShowPercentageInput={fleetRuleRightSideOptions.shouldShowPercentageInput}
					shouldShowRemoveDraggableOption={fleetRuleRightSideOptions.shouldShowRemoveDraggableOption}
					handleRemoveDraggableOption={fleetRuleRightSideHandlers.handleRemoveDraggableOption}
					featureFlags={featureFlags}
					isDragDisabled={fleetRuleRightSideOptions.isDragDisabled || disabled}
					invalidPercents={fleetRuleRightSideOptions.invalidPercents}
				/>

				{featureFlags?.enableRatioPercentage &&
					(fleetRuleRightSideOptions.showPercentageTips ||
						fleetRuleRightSideOptions.showZeroPercentageError) &&
					!disabled && (
						<div
							className={styles['ratio-bottom-total-percentage-allocation']}
							data-test-id="ratio-bottom-total-percentage-allocation"
						>
							<div
								className={classNames(styles['ratio-bottom-allocation'], {
									[styles[
										'ratio-bottom-allocation-error'
									]]: fleetRuleRightSideOptions.draggableOptions.some(item => item.error === true)
								})}
							>
								{translations.ratioAllocation}
							</div>
							<div
								className={classNames(styles['ratio-bottom-total-percentage'], {
									[styles['ratio-bottom-total-percentage-error']]:
										fleetRuleRightSideOptions.invalidPercents
								})}
								data-test-id={
									fleetRuleRightSideOptions.invalidPercents
										? 'ratio-bottom-total-percentage-error'
										: 'ratio-bottom-total-percentage'
								}
							>
								{translations.totalPercentageError}
							</div>
						</div>
					)}

				<div data-test-id="fleet-router-rule-body-right-side-checkbox" className={styles['checkbox-container']}>
					<CheckboxAttribute
						disabled={disabled}
						onChangeCheckbox={fleetRuleRightSideHandlers.onChangeCheckbox}
						checked={fleetRuleRightSideOptions.rightCheckboxValue}
						title={translations.fallback}
						tooltipText={translations.tooltipText}
					/>
				</div>
				<div
					data-test-id="fleet-router-rule-body-right-side-radio-group-bottom"
					className={styles['fallback-radio-group']}
				>
					<FleetRadioGroup
						disabled={disabled || !fleetRuleRightSideOptions.rightCheckboxValue}
						handleChange={fleetRuleRightSideHandlers.handleFallbackChange}
						options={fleetRuleRightSideOptions.fallbackRadioOptions}
						value={fleetRuleRightSideOptions.fallbackRadioValue}
					/>
				</div>
			</div>
		)}
	</>
);
